/**
 * Repairs Tracking — full-tab entry point for the "Repair & Preventative
 * Work" section that previously only existed buried inside the Awaab's
 * Law modal (opened via the small icon in the Tenants list).
 *
 * Labelled with the RP01/RP02 identifier (the RSH's own Repairs TSM
 * measure codes, already used elsewhere in this system's TSM reporting)
 * so it reads as the technical repairs-SLA tracking view it actually is.
 * Repair work itself is logged inside a hazard case's "Repair &
 * Preventative Work" section (AwaabPanel) — same as Awaab's Law's own
 * tab, this page includes the same tenant picker so a user can actually
 * get into a case and log/act on a repair from here, not just read about
 * one.
 */
import React, { useEffect, useMemo, useState } from 'react';
import Navbar from '../Dashboard/components/Navbar';
import Footer from '../Dashboard/components/Footer';
import { awaabService, tenantService, repairsService } from '../../services/api';
import AwaabPanel from '../Awaab/AwaabPanel';
import SafeguardingPanel from '../RSL/SafeguardingPanel';
import CSVImportModal from '../Shared/CSVImportModal';
import { attachEvidenceFromZip } from '../../utils/bulkEvidenceUpload';

interface Tenant {
    id: string;
    name: string;
    email: string;
    propertyId: string;
}

interface AttentionCase {
    caseId: string;
    hazardType: string;
    classification: string;
    worstStatus: 'BREACHED' | 'AT_RISK';
    earliestDue: string | null;
}

export default function RepairsTrackingPage() {
    const [dashboard, setDashboard] = useState<any>(null);
    const [attention, setAttention] = useState<AttentionCase[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [tenantsLoading, setTenantsLoading] = useState(true);
    const [search, setSearch] = useState('');

    const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
    const [showPanel, setShowPanel] = useState(false);
    const [selectedTenantForSafeguarding, setSelectedTenantForSafeguarding] = useState<Tenant | null>(null);
    const [showSafeguardingPanel, setShowSafeguardingPanel] = useState(false);

    // "Log or update a repair" CTA — opens blank, tenant search only, so a
    // repair can be associated with a tenant in one step instead of
    // scrolling down to the always-visible picker below.
    const [showQuickPicker, setShowQuickPicker] = useState(false);
    const [quickSearch, setQuickSearch] = useState('');
    const [showBulkImport, setShowBulkImport] = useState(false);

    useEffect(() => {
        let cancelled = false;
        awaabService.getPortfolioDashboard()
            .then((res: any) => {
                if (cancelled) return;
                const data = res.data?.data;
                setDashboard({
                    open: data?.portfolio?.totalOpenCases,
                    closed: data?.portfolio?.totalClosedCases,
                    breached: data?.slaHealth?.breached,
                    atRisk: data?.slaHealth?.atRisk,
                    onTrack: data?.slaHealth?.onTrack,
                });
                setAttention(data?.attentionRequired || []);
            })
            .catch(() => {
                if (cancelled) return;
                awaabService.getDashboard()
                    .then((res: any) => { if (!cancelled) setDashboard(res.data?.data || null); })
                    .catch((e: any) => { if (!cancelled) setError(e?.response?.data?.message || 'Failed to load repairs data'); });
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        let cancelled = false;
        tenantService.getTenants()
            .then((res: any) => { if (!cancelled) setTenants(res.data?.data || res.data || []); })
            .catch(() => { if (!cancelled) setTenants([]); })
            .finally(() => { if (!cancelled) setTenantsLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const filteredTenants = useMemo(() => {
        if (!search.trim()) return tenants.slice(0, 12);
        const q = search.toLowerCase();
        return tenants.filter((t) => t.name?.toLowerCase().includes(q) || t.email?.toLowerCase().includes(q)).slice(0, 12);
    }, [tenants, search]);

    const quickFilteredTenants = useMemo(() => {
        if (!quickSearch.trim()) return [];
        const q = quickSearch.toLowerCase();
        return tenants.filter((t) => t.name?.toLowerCase().includes(q) || t.email?.toLowerCase().includes(q)).slice(0, 8);
    }, [tenants, quickSearch]);

    const openTenant = (tenant: Tenant) => {
        setSelectedTenant(tenant);
        setShowPanel(true);
    };

    const openSafeguarding = (tenant: Tenant) => {
        setSelectedTenantForSafeguarding(tenant);
        setShowSafeguardingPanel(true);
    };

    const openTenantFromQuickPicker = (tenant: Tenant) => {
        setShowQuickPicker(false);
        setQuickSearch('');
        openTenant(tenant);
    };

    return (
        <div className="min-h-screen bg-slate-50">
            <Navbar />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="mb-2 flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900">
                            <i className="fas fa-toolbox text-blue-600 mr-2"></i>Repairs Tracking
                        </h1>
                        <p className="text-slate-500 mt-1">Portfolio-wide progress on safety and preventative repair work, and the SLA deadline each item is tracked against.</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setShowBulkImport(true)}
                            title="Bulk upload existing repair records from a CSV"
                            className="shrink-0 inline-flex items-center gap-2 bg-white hover:bg-slate-50 text-blue-700 font-semibold px-4 py-2.5 rounded-xl shadow-sm border border-blue-200 transition-colors"
                        >
                            <i className="fas fa-file-csv"></i> Bulk Upload
                        </button>
                        <button
                            onClick={() => setShowQuickPicker(true)}
                            className="shrink-0 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2.5 rounded-xl shadow-sm transition-colors"
                        >
                            <i className="fas fa-plus"></i> Log or update a repair
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="mt-6 bg-white rounded-2xl border border-slate-200 shadow-sm p-6 text-slate-400 text-sm">Loading repairs data…</div>
                ) : error ? (
                    <div className="mt-6 bg-white rounded-2xl border border-slate-200 shadow-sm p-6 text-red-600 text-sm">{error}</div>
                ) : (
                    <>
                        <div className="mt-6">
                            <h3 className="text-lg font-bold text-slate-900 mb-3">Repair queue — needs action now</h3>
                            {attention.length === 0 ? (
                                <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-sm text-slate-500">
                                    <i className="fas fa-circle-check text-2xl text-emerald-300 mb-2 block"></i>
                                    Nothing breached or at risk — every open repair is currently within its SLA window.
                                </div>
                            ) : (
                                <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
                                    {attention.map((c) => (
                                        <div key={c.caseId} className="flex items-center justify-between px-5 py-3">
                                            <div>
                                                <span className={`inline-block w-2 h-2 rounded-full mr-2 ${c.worstStatus === 'BREACHED' ? 'bg-red-500' : 'bg-amber-500'}`}></span>
                                                <span className="font-medium text-slate-800">{c.hazardType}</span>
                                                <span className="text-slate-400 text-sm ml-2">{c.classification}</span>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${c.worstStatus === 'BREACHED' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                                                    {c.worstStatus === 'BREACHED' ? 'Breached' : 'At risk'}
                                                </span>
                                                <span className="text-sm text-slate-500 w-24 text-right">{c.earliestDue ? new Date(c.earliestDue).toLocaleDateString('en-GB') : '—'}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <p className="text-xs text-slate-400 mt-3">
                                To act on a specific repair, use the search below to find the tenant and open their case.
                            </p>
                        </div>

                        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="bg-white rounded-xl border border-slate-200 p-5">
                                <div className="text-3xl font-bold text-slate-900">{dashboard?.open ?? '—'}</div>
                                <div className="text-sm text-slate-500 mt-1">Repairs in progress</div>
                            </div>
                            <div className="bg-white rounded-xl border border-emerald-200 p-5">
                                <div className="text-3xl font-bold text-emerald-600">{dashboard?.onTrack ?? '—'}</div>
                                <div className="text-sm text-slate-500 mt-1">On track</div>
                            </div>
                            <div className="bg-white rounded-xl border border-amber-200 p-5">
                                <div className="text-3xl font-bold text-amber-600">{dashboard?.atRisk ?? '—'}</div>
                                <div className="text-sm text-slate-500 mt-1">At risk of SLA breach</div>
                            </div>
                            <div className="bg-white rounded-xl border border-red-200 p-5">
                                <div className="text-3xl font-bold text-red-600">{dashboard?.breached ?? '—'}</div>
                                <div className="text-sm text-slate-500 mt-1">SLA breached</div>
                            </div>
                        </div>
                    </>
                )}

                {/* Primary action — repair work is logged inside a tenant's
                    hazard case (AwaabPanel's "Repair & Preventative Work"
                    section), same entry point as Awaab's Law and the small
                    icon in the Tenants list. */}
                <div id="repairs-tenant-picker" className="mt-8 bg-white rounded-2xl border border-blue-200 shadow-sm p-5">
                    <h3 className="text-base font-bold text-slate-900 mb-1">Find a tenant to log or update repair work</h3>
                    <p className="text-sm text-slate-500 mb-4">Search for the tenant, then click them to open their case and log safety or preventative work against it.</p>
                    <div className="relative mb-4">
                        <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by tenant name or email…"
                            className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                        />
                    </div>

                    {tenantsLoading ? (
                        <div className="text-sm text-slate-400 py-4">Loading tenants…</div>
                    ) : filteredTenants.length === 0 ? (
                        <div className="text-sm text-slate-400 py-4">No matching tenants.</div>
                    ) : (
                        <div className="divide-y divide-slate-100">
                            {filteredTenants.map((t) => (
                                <div key={t.id} className="flex items-center justify-between px-2 py-3 hover:bg-slate-50 rounded-lg transition-colors">
                                    <div>
                                        <div className="text-sm font-medium text-slate-800">{t.name}</div>
                                        <div className="text-xs text-slate-400">{t.email}</div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => openSafeguarding(t)}
                                            title="Safeguarding & Pre-Action Protocol"
                                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200 px-3 py-1.5 rounded-lg transition-colors"
                                        >
                                            <i className="fas fa-shield-halved"></i> Safeguarding
                                        </button>
                                        <button
                                            onClick={() => openTenant(t)}
                                            title="Awaab's Law Hazard Management"
                                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 px-3 py-1.5 rounded-lg transition-colors"
                                        >
                                            <i className="fas fa-house-chimney-crack"></i> Hazard Management
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {showQuickPicker && (
                <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
                    <div className="flex items-start justify-center min-h-screen pt-24 px-4 pb-20">
                        <div
                            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                            aria-hidden="true"
                            onClick={() => { setShowQuickPicker(false); setQuickSearch(''); }}
                        ></div>
                        <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg p-5">
                            <div className="flex items-start justify-between mb-4">
                                <div>
                                    <h3 className="text-base font-bold text-slate-900">Log or update a repair</h3>
                                    <p className="text-sm text-slate-500 mt-0.5">Search for the tenant to associate this repair with.</p>
                                </div>
                                <button
                                    onClick={() => { setShowQuickPicker(false); setQuickSearch(''); }}
                                    className="text-slate-400 hover:text-slate-600"
                                >
                                    <i className="fas fa-xmark"></i>
                                </button>
                            </div>
                            <div className="relative mb-3">
                                <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
                                <input
                                    type="text"
                                    value={quickSearch}
                                    onChange={(e) => setQuickSearch(e.target.value)}
                                    placeholder="Search by tenant name or email…"
                                    className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                                    autoFocus
                                />
                            </div>
                            {!quickSearch.trim() ? (
                                <div className="text-sm text-slate-400 py-6 text-center">Start typing a tenant's name or email.</div>
                            ) : quickFilteredTenants.length === 0 ? (
                                <div className="text-sm text-slate-400 py-6 text-center">No matching tenants.</div>
                            ) : (
                                <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
                                    {quickFilteredTenants.map((t) => (
                                        <button
                                            key={t.id}
                                            onClick={() => openTenantFromQuickPicker(t)}
                                            className="w-full flex items-center justify-between px-2 py-3 hover:bg-slate-50 rounded-lg transition-colors text-left"
                                        >
                                            <div>
                                                <div className="text-sm font-medium text-slate-800">{t.name}</div>
                                                <div className="text-xs text-slate-400">{t.email}</div>
                                            </div>
                                            <i className="fas fa-chevron-right text-slate-300 text-xs"></i>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <AwaabPanel isOpen={showPanel} onClose={() => setShowPanel(false)} tenant={selectedTenant} />
            <SafeguardingPanel isOpen={showSafeguardingPanel} onClose={() => setShowSafeguardingPanel(false)} tenant={selectedTenantForSafeguarding} />

            <CSVImportModal
                isOpen={showBulkImport}
                onClose={() => setShowBulkImport(false)}
                onImport={async (data, zipFile) => {
                    let rows: any[] = data.map((row: any, idx: number) => {
                        const category = String(row.category || '').toLowerCase().trim();
                        if (!['emergency', 'urgent', 'routine', 'planned', 'cyclical'].includes(category)) {
                            throw new Error(`Row ${idx + 1}: category must be emergency, urgent, routine, planned or cyclical`);
                        }
                        const status = String(row.status || 'open').toLowerCase().trim();
                        if (!['open', 'closed'].includes(status)) throw new Error(`Row ${idx + 1}: status must be open or closed`);
                        return {
                            propertyId: row.property_id,
                            category,
                            description: row.description || '',
                            status,
                            openedAt: row.date_reported || undefined,
                            closedAt: status === 'closed' ? (row.date_completed || undefined) : undefined,
                            evidence_filename: row.evidence_filename || '',
                        };
                    });
                    rows = await attachEvidenceFromZip(rows, zipFile, {
                        filenameField: 'evidence_filename',
                        resolvePropertyId: (row) => row.propertyId,
                    });
                    const repairs = rows.map(({ evidence_filename, _attachStatus, ...rest }) => rest);
                    await repairsService.bulkImport(repairs);
                }}
                title="Bulk Upload Repairs"
                headerRequirements={[
                    { id: 'property_id', label: 'Property ID', required: true, description: 'The property this repair belongs to', example: 'PROP-0001' },
                    { id: 'category', label: 'Category', required: true, description: 'emergency, urgent, routine, planned or cyclical', example: 'urgent' },
                    { id: 'description', label: 'Description', required: false, description: 'What the repair is', example: 'Leaking kitchen tap' },
                    { id: 'status', label: 'Status', required: false, description: 'open or closed (defaults to open)', example: 'open' },
                    { id: 'date_reported', label: 'Date Reported', required: false, description: 'YYYY-MM-DD', example: '2026-01-15' },
                    { id: 'date_completed', label: 'Date Completed', required: false, description: 'YYYY-MM-DD, only if status is closed', example: '2026-01-20' },
                    { id: 'evidence_filename', label: 'Evidence Filename', required: false, description: 'Filename inside the companion .zip to attach as this repair\'s photo/document', example: 'tap_leak_photo.jpg' },
                ]}
                templateHeaders={['property_id', 'category', 'description', 'status', 'date_reported', 'date_completed', 'evidence_filename']}
                sampleData="PROP-0001,urgent,Leaking kitchen tap,open,2026-01-15,,tap_leak_photo.jpg"
                allowFileAttachments
            />

            <Footer />
        </div>
    );
}
